import { useState, useEffect, useCallback } from 'react';

declare const __APP_VERSION__: string;

interface VersionCheckOptions {
  /** Interval in milliseconds to check for updates. Default: 60000 (1 minute) */
  checkInterval?: number;
  /** Whether to enable the version check. Default: true */
  enabled?: boolean;
}

interface VersionCheckResult {
  /** Whether a new version is available */
  hasNewVersion: boolean;
  /** The current version running in the app */
  currentVersion: string;
  /** The new version available (if any) */
  newVersion: string | null;
  /** Function to manually check for updates */
  checkForUpdates: () => Promise<void>;
  /** Function to hard refresh the page */
  hardRefresh: () => void;
  /** Dismiss the update notification */
  dismiss: () => void;
}

interface VersionJsonResponse {
  version: string;
  buildTime?: string;
}

const VERSION_FILE_PATH = '/version.json';

/**
 * Hook to detect when a new version of the app is available.
 * It periodically fetches the version.json file and compares it with the current version.
 */
export function useVersionCheck(options: VersionCheckOptions = {}): VersionCheckResult {
  const { checkInterval = 60000, enabled = true } = options;

  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const currentVersion = __APP_VERSION__;

  const checkForUpdates = useCallback(async () => {
    if (!currentVersion) {
      console.warn('[VersionCheck] Current version is not defined.');
      return;
    }

    try {
      // Add cache-busting query parameter to avoid cached responses
      const response = await fetch(`${VERSION_FILE_PATH}?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (!response.ok) {
        console.warn('[VersionCheck] Failed to fetch version file:', response.status);
        return;
      }

      const data = (await response.json()) as VersionJsonResponse;
      const serverVersion = data.version;

      if (serverVersion && serverVersion !== currentVersion) {
        console.info(`[VersionCheck] New version available: ${serverVersion} (current: ${currentVersion})`);
        setNewVersion(serverVersion);
        setHasNewVersion(true);
      }
    } catch (error) {
      // Silently fail - version check is not critical
      console.warn('[VersionCheck] Error checking for updates:', error);
    }
  }, [currentVersion]);

  const hardRefresh = useCallback(() => {
    // Clear all caches and reload
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    // Force a hard reload bypassing the cache
    window.location.reload();
  }, []);

  const dismiss = useCallback(() => {
    setHasNewVersion(false);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Initial check after a short delay
    const initialTimeout = setTimeout(() => {
      void checkForUpdates();
    }, 5000);

    // Set up periodic checks
    const intervalId = setInterval(() => {
      void checkForUpdates();
    }, checkInterval);

    // Also check when the tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdates();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, checkInterval, checkForUpdates]);

  return {
    hasNewVersion,
    currentVersion,
    newVersion,
    checkForUpdates,
    hardRefresh,
    dismiss,
  };
}
